/**
 * Which folders may receive the PDF report produced by completing a checklist.
 *
 * Step 3 of the completion flow used to offer every folder in the tree, so a
 * report could be filed into an article folder, a "checklist under completion"
 * folder, a laws folder and so on. Those folders are read back through
 * different endpoints than plain documents, which left the mobile document
 * viewer crashing on files it could not resolve, and left the report sitting
 * under a folder type that cannot hold one.
 *
 * Eligibility follows the tree's own colour scheme, where `getFileIcon` paints
 * `article` blue, `checklist` and the under-completion folders red,
 * `laws`/`regulation` grey, `videos` green, and lets everything else fall
 * through to the plain yellow document folder. Only the yellow ones can hold a
 * report, so the special-purpose types are named here and anything else is
 * allowed - the same fallback the icon map uses, which also means an
 * unrecognised type can never leave the picker empty.
 *
 * Kept in step with the Flutter app's `CompletionTargetFolder`.
 */

/** Folder types that serve a specific purpose and cannot hold a report. */
const SPECIAL_PURPOSE_DOCUMENT_TYPES = [
  'article',
  'checklist',
  'laws',
  'regulation',
  'videos',
  'archiveundercompletion',
  'archiveundercompletionfolder',
  'fsundercompletion',
  'manualfsundercompletion',
  'undercompletion',
  'vehiclecontrolundercompletion',
  'installationform',
  'undercompletioninstallationform'
];

/**
 * Whether a folder may be picked as the destination for a completed checklist.
 *
 * `translateKey` is checked as well as `documentType` because the "under
 * completion" folders are identified by key rather than by type in several
 * modules - their `DocumentTypeId` varies per tenant, but the key does not. The
 * `UNDER_COMPLETION` marker deliberately does not match the `*_COMPLETED` /
 * `COMPLETED_*` keys, which are valid destinations: a completed report belongs
 * in "Checklists completed", never in the drafts folder it was resumed from.
 */
export function acceptsCompletedChecklist(node: any): boolean {
  if (!node) {
    return false;
  }

  const translateKey = (node.translateKey || '').toString().trim().toUpperCase();
  if (translateKey.indexOf('UNDER_COMPLETION') > -1) {
    return false;
  }

  const documentType = (node.documentType || '')
    .toString()
    .trim()
    .toLowerCase();
  if (!documentType) {
    return true;
  }
  return SPECIAL_PURPOSE_DOCUMENT_TYPES.indexOf(documentType) === -1;
}

/**
 * Recursively drops folders that cannot receive a completed checklist report.
 *
 * Returns a new array; the input nodes are copied rather than mutated, since
 * the same tree data is bound elsewhere in the completion screen.
 */
export function filterCompletionTargetFolders(nodes: any): any[] {
  if (!Array.isArray(nodes)) {
    return [];
  }
  return nodes.filter(acceptsCompletedChecklist).map(node => ({
    ...node,
    nodes: Array.isArray(node.nodes)
      ? filterCompletionTargetFolders(node.nodes)
      : node.nodes
  }));
}
